import { useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';

/**
 * Data Room: files/folders selected, bulk-action footer bar showing — set in FileExplorer.
 * AskAicaV2 hides its floating icon while this is set (isAnyDrawerOpen).
 */
export const DATA_ROOM_BULK_ACTION_PARAM = 'dataRoomBulkAction';

/** Publishes `?<paramName>=true` while a drawer/selection is open — AskAicaV2 hides its floating
 * icon off flags like this (isAnyDrawerOpen) — and clears it again, including on unmount. */
export const useDrawerUrlFlag = (isOpen: boolean, paramName: string) => {
    const [searchParams, setSearchParams] = useSearchParams();

    const isFlagPublished = useRef(false);

    // Do NOT depend the cleanup below on `setSearchParams`: react-router gives it a new identity on
    // every search-param write, so the cleanup would run while still mounted and undo the flag.
    const setSearchParamsRef = useRef(setSearchParams);
    useEffect(() => {
        setSearchParamsRef.current = setSearchParams;
    }, [setSearchParams]);

    useEffect(() => {
        const nextParams = new URLSearchParams(searchParams);
        const isFlagged = nextParams.get(paramName) === 'true';
        isFlagPublished.current = isFlagged;
        if (isOpen && !isFlagged) {
            nextParams.set(paramName, 'true');
            isFlagPublished.current = true;
            setSearchParams(nextParams, { replace: true });
        } else if (!isOpen && isFlagged) {
            nextParams.delete(paramName);
            isFlagPublished.current = false;
            setSearchParams(nextParams, { replace: true });
        }
    }, [isOpen, paramName, searchParams, setSearchParams]);

    // A tab switch (or navigating away mid-selection) unmounts the caller, so the effect above
    // can't clear the flag on the way out and it would carry over into the next view.
    useEffect(
        () => () => {
            if (!isFlagPublished.current) return;
            isFlagPublished.current = false;
            setSearchParamsRef.current(
                (previous) => {
                    const nextParams = new URLSearchParams(previous);
                    nextParams.delete(paramName);
                    return nextParams;
                },
                { replace: true },
            );
        },
        [paramName],
    );
};

export default useDrawerUrlFlag;
